import { on, createReducer, createFeature, createSelector } from '@ngrx/store'
import { EntityState, EntityAdapter, createEntityAdapter, Dictionary } from '@ngrx/entity'
import { Article } from './article.model'
import * as actions from './article.actions'
import { NamedSearchParameters } from '../shared/components/filtering-search-component-base/filtering-search-types'

export interface ListingState {
  window: number
  present: number
  total: number
  historyID?: number
  dataSetSize?: number
  showing?: string
  orderedBy?: { column: string; direction: string }
}
export interface ArticlesState extends EntityState<Article> {
  currentArticle: Article | undefined,
  listing : ListingState,
  search: NamedSearchParameters,
  area: string,
  status: {
    loading: boolean
    loadingAnArticle: boolean
    inflight: number
    error: boolean
    downloadID: string
    page: number
  }
  extraData: Dictionary<any>,
}

export const adapter: EntityAdapter<Article> = createEntityAdapter<Article>()

export const initialState: ArticlesState = {
  ids: [
  ],
  entities: {
  },
  currentArticle: undefined,
  listing: {
    window: 50,
    present: 0,
    total: 0,
    historyID: undefined,
    dataSetSize: undefined,
    showing: undefined
  },
  area: '',
  search: {
    parameters: {},
    order: {
      column: '',
      direction: ''
    }
  },
  status: {
    loading: false,
    loadingAnArticle: false,
    inflight: 0,
    error: false,
    downloadID: '',
    page: 0,
  },
  extraData: {}
}
const {
  selectAll
} = adapter.getSelectors()

export const articlesFeature = createFeature({
  name: 'articles',
  reducer: createReducer(
    initialState,
    on(actions.loadArticles, (state, { articles, listing, area, extraData }) => {
      state = adapter.addMany(articles, state)

      let currentArticle = undefined
      if (state.currentArticle && state.entities[state.currentArticle.id]) {
        currentArticle = {
          ...state.entities[state.currentArticle.id],
          ...state.currentArticle
        }
      }

      let listingToUse = structuredClone(listing ?? state.listing)
      if (listingToUse.total === -1) {
        listingToUse.total = state.listing.total
      }
      return {
        ...state,
        area: area ?? state.area,
        listing: listingToUse,
        extraData: extraData ?? state.extraData,
        currentArticle
      }
    }),
    on(actions.loadAnArticle, (state, { article, listing, area, extraData }) => {
      const finalArticle = {
        ...state.entities[article.id],
        ...article
      }
      state = adapter.upsertOne(finalArticle, state)
      return {
        ...state,
        area: area ?? state.area,
        listing: listing ?? state.listing,
        extraData: extraData ?? state.extraData,
        currentArticle: finalArticle
      }
    }),
    on(actions.setCurrentArticle, (state, {id}) => {
      return {
        ...state,
        currentArticle: state.entities[id] ,
      }
    }),
    on(actions.loadArticlesRemote, (state) => {
      return {
        ...state,
        status: {
          ...state.status,
          loading: true,
          inflight: state.status.inflight + 1,
          error: false,
          downloadID: '',
          page: state.status.page + 1
        }
      }
    }),
    on(actions.loadAnArticleRemote, (state) => {
      return {
        ...state,
        status: {
          ...state.status,
          loadingAnArticle: true,
          error: false,
          downloadID: '',
        }
      }
    }),
    on(actions.removeAllArticles, (state) => {
      return {
        ...state,
        ids: [],
        entities: {},
        listing: {
          window: 50,
          present: 0,
          total: 0,
          page: 0,
          historyID: undefined,
          dataSetSize: undefined,
          showing: undefined
        },
        status: {
          loading: false,
          loadingAnArticle: false,
          inflight: 0,
          error: false,
          downloadID: '',
          page: 0
        }
      }
    }),
    on(actions.resetArticleState, (state) => {
      return initialState
    }),
    on(actions.updateSearchParameters, (state, {parameters, area, order})=>{
      return {
        ...state,
        area,
        status: {
          ...state.status,
          loading: true,
          inflight: state.status.inflight + 1,
          error: false,
          downloadID: '',
          page: 0,
        },
        search: {
          parameters,
          order
        }
      }
    }),
    on(actions.setSearchParameters, (state, {parameters, area, order})=>{
      return {
        ...state,
        area,
        status: {
          ...state.status,
          loading: true,
          inflight: state.status.inflight + 1,
          error: false,
          downloadID: '',
          page: 0,
        },
        search: {
          parameters,
          order
        }
      }
    }),
    on(actions.articlesLoaded, (state) => {
      return {
        ...state,
        status: {
          ...state.status,
          inflight: state.status.inflight - 1,
          // loading: state.status.inflight > 1 -- we need to detect cancelled connections or this does not go to zero
          loading: false,
        },
      }
    }),
    on(actions.articleLoaded, (state) => {
      return {
        ...state,
        status: {
          ...state.status,
          loadingAnArticle: false,
        }
      }
    }),
    on(actions.articlesLoadedError, (state) => {
      return {
        ...state,
        status: {
          loading: false,
          loadingAnArticle: false,
          inflight: 0,
          error: true,
          downloadID: '',
          page: 0,
        }
      }
    }),
    on(actions.downloadInProgress, (state, {downloadID}) => {
      return {
        ...state,
        status: {
          downloadID,
          loading: true,
          loadingAnArticle: false,
          inflight: 1,
          error: false,
          page: 0
        }
      }
    }),
    on(actions.downloadFinished, (state  => {
      return {
        ...state,
        status: {
          downloadID: '',
          loading: false,
          loadingAnArticle: false,
          inflight: 0,
          error: false,
          page: 0
        }
      }
    }))
  ),
  extraSelectors: ({ selectArticlesState, selectEntities }) => ({
    selectListingState: createSelector(
      selectArticlesState,
      (state: ArticlesState) => state.listing
    ),
    selectStatusState: createSelector(
      selectArticlesState,
      (state: ArticlesState) => state.status
    ),
    selectSearchState: createSelector(
      selectArticlesState,
      (state: ArticlesState) => state.search
    ),
    selectAllArticles: createSelector(
      selectArticlesState,
      (state: ArticlesState) => selectAll(state).sort( (a, b) => a.runningOrder - b.runningOrder)
    ),
    selectAnArticleById: <Article>(id: number) => createSelector(selectEntities, entities => entities[id] as Article),
    selectCurrentArticle: <Article>() => createSelector(selectArticlesState, state => state.currentArticle) as Article,
    selectAnArticleByRunningOrder: <Article>(runningOrder: number) => createSelector(selectEntities, entities => Object.values(entities).find( e => e!.runningOrder == runningOrder) as Article),
    selectPreviousAndNextByRunningOrder: (runningOrder: number) => createSelector(selectEntities, entities => {
      let previousID = '', nextID = ''
      if (runningOrder !== undefined) {
        const entitiesIds = Object.values(entities).sort((a, b) => a!.runningOrder - b!.runningOrder).map( (a) => a!.id)
        if (runningOrder > 0) {
          previousID = entitiesIds[runningOrder - 1]?.toString()
        }
        if (runningOrder < entitiesIds.length - 2) {
          nextID = entitiesIds[runningOrder + 1]?.toString()
        }
      }
      return [previousID, nextID]
    }),
    selectExtraData: createSelector(
      selectArticlesState,
      (state: ArticlesState) => state.extraData
    ),
  })
})

export const {
  name, // feature name
  reducer, // feature reducer
  selectArticlesState,
  selectListingState,
  selectStatusState,
  selectSearchState,
  selectAllArticles,
  selectCurrentArticle,
  selectAnArticleById,
  selectAnArticleByRunningOrder,
  selectPreviousAndNextByRunningOrder,
  selectIds,
  selectListing,
  selectExtraData,
} = articlesFeature